import { inject, Injectable, OnDestroy } from '@angular/core'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { SupportedResolutions } from '@modules/trading-view-module/constants/supported-resolutions'
import { TimeframesWithPrePostLines } from '@modules/trading-view-module/constants/timeframes-with-pre-post-lines'
import { TradingViewTimeframesEnum } from '@modules/trading-view-module/enums/trading-view-timeframes.enum'
import { TickerRefPolygonItemInterface } from '@modules/trading-view-module/interfaces/ticker-ref-polygon-item.interface'
import { TickerRefPolygonResponseInterface } from '@modules/trading-view-module/interfaces/ticker-ref-polygon-response.interface'
import { TradingViewHelperService } from '@modules/trading-view-module/services/trading-view-helper.service'
import {
	Bar,
	DatafeedConfiguration,
	ErrorCallback,
	HistoryCallback,
	LibrarySymbolInfo,
	OnReadyCallback,
	PeriodParams,
	ResolutionString,
	ResolveCallback,
	SearchSymbolResultItem,
	SearchSymbolsCallback,
	SubscribeBarsCallback,
} from '@public/trading-view/charting_library'
import { BehaviorSubject, Subject, Subscription, takeUntil } from 'rxjs'
import { SocketStockEvEnum } from '@modules/prices-module/socket-prices/enums/socket-stock-ev.enum'
import { SocketStocksType } from '@modules/prices-module/socket-prices/types/socket-stocks.type'
import { StocksService } from '@modules/stocks-module/general/services/stocks.service'
import { LatestPricesResponseInterface } from '@modules/stocks-module/general/interfaces/latest-prices-response.interface'
import { LatestPricesInterface } from '@modules/stocks-module/general/interfaces/latest-prices.interface'
import { SocketStockAInterface } from '@modules/prices-module/socket-prices/interfaces/socket-stock-a.interface'
import { SocketStockTInterface } from '@modules/prices-module/socket-prices/interfaces/socket-stock-t.interface'
import { SocketPricesHelperService } from '@modules/prices-module/socket-prices/services/socket-prices-helper.service'
import { FromToInterface } from '../interfaces/from-to.interface'
import { HttpErrorResponse } from '@angular/common/http'

@Injectable()
export class TradingViewDatafeedService implements OnDestroy {
	protected readonly math: Math = Math
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum
	protected readonly socketStockEvEnum: typeof SocketStockEvEnum = SocketStockEvEnum
	protected readonly supportedResolutions: TradingViewTimeframesEnum[] = SupportedResolutions
	protected readonly timeframesWithPrePostLines: TradingViewTimeframesEnum[] = TimeframesWithPrePostLines

	public socketPricesStocks$: BehaviorSubject<SocketStocksType[]> = new BehaviorSubject<SocketStocksType[]>([])

	private readonly _stocksService: StocksService = inject<StocksService>(StocksService)
	private readonly _socketPricesHelperService: SocketPricesHelperService =
		inject<SocketPricesHelperService>(SocketPricesHelperService)
	private readonly _tradingViewHelperService: TradingViewHelperService =
		inject<TradingViewHelperService>(TradingViewHelperService)
	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)

	private _destroy$: Subject<void> = new Subject<void>()
	private _subscriptionBars: Map<string, Subscription> = new Map()

	get marketStatus(): MarketStatusEnum {
		return this._marketStatusHelperService.marketStatus
	}

	public onReady(callback: OnReadyCallback): void {
		const config: DatafeedConfiguration = {
			supported_resolutions: this.supportedResolutions as ResolutionString[],
			supports_marks: false,
			supports_timescale_marks: false,
			supports_time: true,
		}

		setTimeout((): void => callback(config))
	}

	public resolveSymbol(symbolName: string, onResolve: ResolveCallback, onError: ErrorCallback): void {
		if (!symbolName.trim()) return

		this._stocksService
			.searchTickersReferences(symbolName, 'ticker')
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				(response: TickerRefPolygonResponseInterface): void => {
					if (response?.results && response.results.length) {
						const symbol: TickerRefPolygonItemInterface | undefined = response.results[0]

						const symbolInfo: Partial<LibrarySymbolInfo> = {
							name: symbol?.ticker,
							ticker: symbol?.ticker,
							description: symbol?.name,
							minmov: 1,
							pricescale: 100,
							has_intraday: true,
							supported_resolutions: this.supportedResolutions as ResolutionString[],
							volume_precision: 2,
							has_weekly_and_monthly: true,
						}

						onResolve(symbolInfo as LibrarySymbolInfo)
					} else {
						onError('Symbol not found')
					}
				},
				(err: string): void => onError(err),
			)
	}

	public getBars(
		symbolInfo: LibrarySymbolInfo,
		resolution: ResolutionString,
		periodParams: PeriodParams,
		onResult: HistoryCallback,
		onError: ErrorCallback,
	): void {
		const symbol: string = symbolInfo.ticker as string
		const barKey: string = `${symbol}_${resolution}`
		const { fromUnix, toUnix }: FromToInterface = this._tradingViewHelperService.getNormalizedDates(
			resolution as TradingViewTimeframesEnum,
			periodParams,
		)

		if (fromUnix >= toUnix) {
			onResult([], { noData: true })
			return
		}

		const urlBars: string = this._tradingViewHelperService.generateUrlBars(
			resolution as TradingViewTimeframesEnum,
			fromUnix,
			toUnix,
		)

		this._stocksService.getTickerHistory(symbol, urlBars, resolution as TradingViewTimeframesEnum).subscribe(
			(response: LatestPricesResponseInterface): void => {
				if (!response?.results?.length) {
					onResult([], { noData: false })
					return
				}

				const bars: Bar[] = response.results
					.reverse()
					.map(({ t: time, o: open, h: high, l: low, c: close, v: volume }: LatestPricesInterface): Bar => {
						return { time, open, high, low, close, volume }
					})
				const lastBar: Bar | undefined = bars.slice(-1)[0]

				if (lastBar && fromUnix > lastBar.time) {
					onResult([], { noData: false })
					return
				}

				if (!this._tradingViewHelperService.lastBars.get(barKey) && lastBar) {
					this._tradingViewHelperService.lastBars.set(barKey, lastBar)
				}

				onResult(
					bars.sort((a: Bar, b: Bar): number => a.time - b.time),
					{ noData: false },
				)
			},
			(err: string): void => onError(err),
		)
	}

	public searchSymbols(
		userInput: string,
		exchange: string,
		symbolType: string,
		onResult: SearchSymbolsCallback,
	): void {
		this._stocksService
			.searchTickersReferences(userInput, 'search')
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				(response: TickerRefPolygonResponseInterface): void => {
					if (response.results && response.results.length > 0) {
						const symbols: SearchSymbolResultItem[] = response.results.map(
							(result: TickerRefPolygonItemInterface): SearchSymbolResultItem => ({
								symbol: result.ticker,
								full_name: result.name,
								description: result.name,
								exchange: result.primary_exchange || exchange,
								ticker: result.ticker,
								type: symbolType,
							}),
						)
						onResult(symbols)
					} else {
						onResult([])
					}
				},
				(error: HttpErrorResponse): void => {
					console.error('Error fetching symbols from Polygon.io', error)
					onResult([])
				},
			)
	}

	public subscribeBars(
		symbolInfo: LibrarySymbolInfo,
		resolution: ResolutionString,
		onTick: SubscribeBarsCallback,
		listenerGuid: string,
	): void {
		const marketStatus: MarketStatusEnum = this.marketStatus
		if (marketStatus === this.marketStatusEnum.marketClose) return

		const isPrePostSupportedTimeframe: boolean = this.timeframesWithPrePostLines.includes(
			resolution as TradingViewTimeframesEnum,
		)

		if (isPrePostSupportedTimeframe && marketStatus !== this.marketStatusEnum.marketOpen) return

		const symbol: string | undefined = symbolInfo.ticker
		const barKey: string = `${symbol}_${resolution}`
		const subscriptionKey: string = `${symbol}_${listenerGuid}`

		const subscription: Subscription = this.socketPricesStocks$.subscribe((stocks: SocketStocksType[]): void => {
			const filteredStockBySymbol: SocketStocksType[] = stocks.filter(
				(stockData: SocketStocksType): boolean => stockData.sym === symbol,
			)

			const stockA: SocketStockAInterface | undefined = this._socketPricesHelperService.getStockByEV(
				this.socketStockEvEnum.A,
				filteredStockBySymbol,
			) as SocketStockAInterface | undefined

			const stockT: SocketStockTInterface | undefined = this._socketPricesHelperService.getStockByEV(
				this.socketStockEvEnum.T,
				filteredStockBySymbol,
			) as SocketStockTInterface | undefined

			if (!stockA && !stockT) return

			const lastBar: Bar | undefined = this._tradingViewHelperService.lastBars.get(barKey)
			const lastBarTime: number = this._tradingViewHelperService.getTimeLastBar(resolution, lastBar?.time)

			this._tradingViewHelperService.buildRealtimeBar(barKey, lastBarTime, stockA, stockT, onTick)
		})

		this._subscriptionBars.set(subscriptionKey, subscription)
	}

	public unsubscribeBars(listenerGuid: string): void {
		const foundSubscription: [string, Subscription] | undefined = Array.from(this._subscriptionBars.entries()).find(
			([key]: [string, Subscription]): boolean => key.endsWith(`_${listenerGuid}`),
		)

		if (foundSubscription) {
			const [key, sub]: [string, Subscription] = foundSubscription
			sub.unsubscribe()
			this._subscriptionBars.delete(key)
		}
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
